import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ProductListResponse } from "@/lib/api/types";
import { endpoints } from "@/lib/api/endpoints";
import { apiJsonClient } from "@/lib/query/core/api-json-client";
import { queryKeys } from "@/lib/query/core/query-keys";
import { normalizeSellerProductDetail } from "./useProductByIdQuery";

type UseProductsQueryParams = {
  storeSlug: string;
  limit?: number;
  offset?: number;
  categoryIds?: string[];
  availability?: string;
  status?: string;
  search?: string;
};

type NormalizedFilters = {
  limit?: number;
  offset?: number;
  categoryIds?: string[];
  availability?: string;
  status?: string;
  search?: string;
};

type QueryKeyFilters = {
  limit?: number;
  offset?: number;
  categoryIds?: string;
  availability?: string;
  status?: string;
  search?: string;
};

function normalizeFilters({
  limit,
  offset,
  categoryIds,
  availability,
  status,
  search,
}: Omit<UseProductsQueryParams, "storeSlug">): NormalizedFilters {
  const normalizedSearch = search?.trim();
  const normalizedCategoryIds = categoryIds?.filter((id) => id.trim().length > 0);

  return {
    limit,
    offset,
    categoryIds:
      normalizedCategoryIds && normalizedCategoryIds.length > 0
        ? [...normalizedCategoryIds].sort()
        : undefined,
    availability: availability?.trim() || undefined,
    status: status?.trim() || undefined,
    search:
      normalizedSearch && normalizedSearch.length >= 3 ? normalizedSearch : undefined,
  };
}

function buildPath(filters: NormalizedFilters) {
  const query = new URLSearchParams();
  if (filters.limit !== undefined) {
    query.set("limit", String(filters.limit));
  }
  if (filters.offset !== undefined) {
    query.set("offset", String(filters.offset));
  }
  if (filters.categoryIds) {
    for (const categoryId of filters.categoryIds) {
      query.append("categoryIds[]", categoryId);
    }
  }
  if (filters.availability) {
    query.set("availability", filters.availability);
  }
  if (filters.status) {
    query.set("status", filters.status);
  }
  if (filters.search) {
    query.set("search", filters.search);
  }

  const queryString = query.toString();
  return queryString.length > 0
    ? `${endpoints.seller.products}?${queryString}`
    : endpoints.seller.products;
}

function toQueryKeyFilters(filters: NormalizedFilters): QueryKeyFilters {
  return {
    limit: filters.limit,
    offset: filters.offset,
    categoryIds: filters.categoryIds?.join(","),
    availability: filters.availability,
    status: filters.status,
    search: filters.search,
  };
}

export function useProductsQuery(params: UseProductsQueryParams) {
  const { storeSlug, ...filters } = params;
  const normalizedFilters = normalizeFilters(filters);
  const queryKeyFilters = toQueryKeyFilters(normalizedFilters);

  const query = useQuery({
    queryKey: queryKeys.seller.products(storeSlug, queryKeyFilters),
    queryFn: () => apiJsonClient<ProductListResponse>(buildPath(normalizedFilters)),
    enabled: storeSlug.length > 0,
  });

  // Memoize the normalization so a stable `data` reference survives across
  // renders (otherwise every render produces a fresh array, which cascades
  // through any downstream `useMemo` keyed on `data.items`).
  const data = useMemo(
    () =>
      query.data
        ? {
            ...query.data,
            items: query.data.items.map((item) =>
              normalizeSellerProductDetail(item),
            ),
          }
        : undefined,
    [query.data],
  );

  return {
    data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
